import * as React from 'react';
import { connect } from 'react-redux';
import './Admin.less';
import { Form, Input, Button, Checkbox, Select, DatePicker, Divider, message, Spin, Row, Col, Modal } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import { Moment } from 'moment';
import * as moment from 'moment';
import AddUserDTO from 'src/models/user/AddUserDTO';
import UpdateUserDTO from 'src/models/user/UpdateUserDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import UserApiController from 'src/api/UserApiController';
import GradeApiController from 'src/api/GradeApiController';
import SchoolApiController from 'src/api/SchoolApiController';
import Languages from 'src/constants/Languages';
import ValidEmailDTO from 'src/models/user/ValidEmailDTO';
import { GenericFormItem } from 'src/utils/FormUtil';
import { Grades, Grade } from 'src/constants/Grades';
import { UserState } from 'src/redux/UserReducer';
import { ReduxStoreModel } from 'src/redux/ReduxModel';
import AddEditUserForm from './forms/AddEditUserForm';
import { Roles } from 'src/constants/Roles';
import AccountApiController from "src/api/AccountApiController";
import EmulationDTO from 'src/models/account/EmulationDTO';

// Move Me somewhere global!
export interface AddEditUserStateParams {
    previousLocation?: string;
    previousState?: {};
}

interface RouteParams {
    classroomId: string;
    userId: string;
    isAdmin: string;
}

interface AddEditUserProps extends PageProps<RouteParams, AddEditUserStateParams>, LocalizeContextProps {
    User: UserState;
}

interface AddEditUserState {
    schools: KeyValueDTO<number, string>[];
    currentUser: Partial<UpdateUserDTO>;
    emulationInfo: Partial<EmulationDTO>;

    // Loading indicators
    isLoadingSchools: boolean;
    isLoadingUser: boolean;
    isLoadingEmulation: boolean;
    isSubmitting: boolean;

    isSsoEnabled: boolean;

    // Role warning variables
    hasShownRoleChangeWarning: boolean;
    showModal: boolean;

    // From URL
    userId: number;
    classroomId: number;
    isAdmin: boolean;
}

class AddEditUser extends React.Component<AddEditUserProps, AddEditUserState> {
    constructor(props: AddEditUserProps) {
        super(props);
        bindAllOfThis(this, AddEditUser.prototype);

        let userId = Number(this.props.match.params.userId);
        let classroomId = Number(this.props.match.params.classroomId);
        let isAdmin = this.props.match.params.isAdmin === 'true';
        let isSsoEnabled = this.props.User.isSsoEnabled;

        userId = !isNaN(userId) ? userId : 0;
        classroomId = !isNaN(classroomId) ? classroomId : 0;

        this.state = {
            schools: [],
            currentUser: {
                userId: userId,
                classroomId: classroomId,
            },
            emulationInfo: null,
            isLoadingSchools: true,
            isLoadingUser: true,
            isLoadingEmulation: true,
            isSubmitting: false,
            isSsoEnabled: false, // No idea
            hasShownRoleChangeWarning: false,
            showModal: false,
            userId: userId,
            classroomId: classroomId,
            isAdmin: isAdmin,
        };
    }

    componentWillMount() {
        this.getSchools(this.props.User.districtId);
        this.getEmulationInfo();
        this.fillForm();
    }

    private getSchools(districtId?: number) {
        let userRole = Roles.FindById(this.props.User.roleID);
        // If our role will not allow us to get schools, don't
        if (![Roles.KuderAdmin, Roles.StateAdmin, Roles.DistrictAdmin].some(x => x === userRole)) {
            this.setState({
                schools: [],
                isLoadingSchools: false
            });
            return;
        }

        // If we aren't at a district, we are too broad to load schools
        if (districtId == null) {
            this.setState({
                schools: [],
                isLoadingSchools: false
            });
            return;
        }

        return SchoolApiController.getAllSchoolsByDistrict(districtId).then(schools => {
            this.setState({
                schools: schools.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 ),
                isLoadingSchools: false
            });
        }).catch(e => {
            this.setState({ isLoadingSchools: false });
            message.error(this.props.translate('admin.common.messageSchoolsInDistrictError'));
        });
    }

    private getEmulationInfo() {
        if (this.props.User.isEmulating) {
            return AccountApiController.getCurrentEmulationInfo().then(result => {
                this.setState({
                    emulationInfo: result.data,
                    isLoadingEmulation: false
                });
            });
        } else {
            this.setState({
                emulationInfo: null,
                isLoadingEmulation: false
            });
        }
    }

    private fillForm() {
        if (this.state.userId === 0) {
            return this.setState({
                currentUser: {
                    userId: this.state.userId,
                    classroomId: this.state.classroomId,
                    roleId: this.props.User.roleID
                },
                isLoadingUser: false
            });
        }

        return UserApiController.getUser(this.state.userId).then((result) => {
            this.setState({
                currentUser: result.data,
                isLoadingUser: false
            });
        }).catch(e => {
            message.error(this.props.translate('admin.addEditUser.messageRetrieveError'));
            this.setState({
                currentUser: {
                    userId: this.state.userId,
                    classroomId: this.state.classroomId,
                },
                isLoadingUser: false
            });
        });
    }

    private onModalOk() {
        this.setState({
            hasShownRoleChangeWarning: true,
            showModal: false,
        });
    }

    private handleRoleChange(roleId: number) {
        if (this.state.hasShownRoleChangeWarning || this.state.userId < 1) {
            return;
        }

        this.setState({ showModal: true });
    }

    private handleSubmit(values: UpdateUserDTO) {
        this.setState({ isSubmitting: true });

        if (this.state.userId < 1) {
            // When adding a student, the classroomId will be populated
            if (this.state.classroomId > 0) {
                values.classroomId = this.state.classroomId;
            }

            // When isAdmin is false, we are adding a student
            values.roleId = this.state.isAdmin ? values.roleId : Roles.Student.id;

            return UserApiController.addUser(values)
                .then(result => {
                    this.setState({ isSubmitting: false });
                    if (result.data.isSuccess) {
                        message.success(this.props.translate('admin.addEditUser.messageAdd'));
                        this.handleRedirect();
                    }
                    else {
                        message.error(result.data.errorMessage);
                    }
                }).catch(e => {
                    message.error(this.props.translate('admin.addEditUser.messageAddError'));
                    this.setState({ isSubmitting: false });
                });
        } else {
            return UserApiController.updateUser(values)
                .then(result => {
                    this.setState({ isSubmitting: false });
                    if (result.data.isSuccess) {
                        message.success(this.props.translate('admin.addEditUser.messageUpdate'));
                        this.handleRedirect();
                    }
                    else {
                        let error = result.data.errorMessage || this.props.translate('admin.addEditUser.messageUpdateError');
                        message.error(error);
                    }
                }).catch(e => {
                    message.error(this.props.translate('admin.addEditUser.messageUpdateError'));
                    this.setState({ isSubmitting: false });
                });
        }
    }

    private handleRedirect(): void {
        // We rely on the state parameter to know where to go back to. If not provided, go home
        let state = this.props.location.state;
        let nextLocation = state != null && state.previousLocation != null ? state.previousLocation : Routes.BASE_ROUTE;
        this.props.history.push(nextLocation, state.previousState);
    }

    private renderTitle() {
        // If edit, show name, else show Add Student/Admin
        let user = this.state.currentUser;
        let text = this.state.userId > 0
            ? `${user.firstName} ${user.lastName}` // Editing
            : this.state.isAdmin // Adding
                ? <Translate id="admin.addEditUser.addHeader2" /> // Admin
                : <Translate id="admin.addEditUser.addHeader1" />; // Student
        return <h2>{text}</h2>;
    }

    render() {
        if (this.state.isLoadingSchools || this.state.isLoadingUser || this.state.isLoadingEmulation) {
            return <Spin />;
        }

        return (
            <div>
                {this.renderTitle()}

                <Modal
                    maskClosable={false}
                    visible={this.state.showModal}
                    footer={[
                        <Button key="ok" type="primary" onClick={this.onModalOk}><Translate id="admin.common.okButton" /></Button>
                    ]}>
                    <div>
                        <h3>Warning!</h3>
                        <p>Changing a user's role will change their permissions. Make sure you select the proper role before saving.</p>
                    </div>
                </Modal>

                <Row>
                    <Col xs={24} lg={18} xl={16} xxl={12}>
                        <AddEditUserForm
                            isAdmin={this.state.isAdmin}
                            userData={this.state.currentUser}
                            schools={this.state.schools}
                            emulationInfo={this.state.emulationInfo}
                            isSubmitting={this.state.isSubmitting}
                            onSubmit={this.handleSubmit}
                            onCancel={this.handleRedirect}
                            onRoleChange={this.handleRoleChange}
                        />
                    </Col>
                </Row>
            </div>
        );
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User,
    };
}

export default connect(mapStateToProps)(withLocalize(AddEditUser));



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditUser.tsx